import React, { useState } from 'react'
import { Form, Typography, Flex } from 'antd'
import { PrimaryButton } from '@/shared/ui/components/Button'
import { Icon } from '@/shared/ui/Icon/Icon'
import { MediumInput } from '@/shared/ui/components/Input'
import { FormItem } from '@/shared/ui/forms/FormItem/FormItem'
import { SYSTEM_TYPE } from '@/app/store/systemStore'
import { useOmnicommStore } from '@/app/store/omnicommStore'
import { useAxentaStore } from '@/app/store/axentaStore'
import { useGeliosStore } from '@/app/store/geliosStore'
import {
  BackBtn,
  FormContainer,
  FormInputs,
  FormWrapper,
  LoginHeader,
  Logo,
  AuthContainer,
} from './styled'
import { useBreakpoint } from '@/app/hooks/useBreakpoint'
import { ServerModal } from '@/shared/ui/components/ServerModal/ServerModal'
import {
  AXENTA_DEFAULT_URL,
  FORTMONITOR_DEFAULT_URL,
  GELIOS_DEFAULT_URL,
  OMNICOMM_DEFAULT_URL,
  GLONASSOFT_DEFAULT_URL,
} from '@/shared/config/systemUrls'
import { useFortMonitorStore } from '@/app/store/fortmonitorStore'
import { useGlonassoftStore } from '@/app/store/glonassoftStore'
import { VerifyResult } from '../../model/useSystemAuthCheck'

const { Text, Title } = Typography

const DEFAULT_SERVERS = {
  [SYSTEM_TYPE.OMNICOMM]: OMNICOMM_DEFAULT_URL,
  [SYSTEM_TYPE.AXENTA]: AXENTA_DEFAULT_URL,
  [SYSTEM_TYPE.GELIOS]: GELIOS_DEFAULT_URL,
  [SYSTEM_TYPE.FORTMONITOR]: FORTMONITOR_DEFAULT_URL,
  [SYSTEM_TYPE.GLONASSOFT]: GLONASSOFT_DEFAULT_URL,
} as const

export interface SystemAuthFormData {
  login: string
  password: string
}

interface SystemAuthFormProps {
  systemType:
    | SYSTEM_TYPE.OMNICOMM
    | SYSTEM_TYPE.AXENTA
    | SYSTEM_TYPE.GELIOS
    | SYSTEM_TYPE.FORTMONITOR
    | SYSTEM_TYPE.GLONASSOFT
  onAuthSuccess: (credentials: {
    login: string
    password: string
  }) => Promise<VerifyResult>
  onBack: () => void
}

export const SystemAuthForm: React.FC<SystemAuthFormProps> = ({
  systemType,
  onAuthSuccess,
  onBack,
}) => {
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isServerModalVisible, setIsServerModalVisible] = useState(false)
  const [selectedServer, setSelectedServer] = useState<string>('')

  const omnicommStore = useOmnicommStore()
  const axentaStore = useAxentaStore()
  const geliosStore = useGeliosStore()
  const fortMonitorStore = useFortMonitorStore()
  const glonassoftStore = useGlonassoftStore()
  const { isSm, isXs } = useBreakpoint()
  const isMobile = isXs || isSm

  const isOmnicomm = systemType === SYSTEM_TYPE.OMNICOMM
  const isGelios = systemType === SYSTEM_TYPE.GELIOS
  const isFortMonitor = systemType === SYSTEM_TYPE.FORTMONITOR
  const isGlonassoft = systemType === SYSTEM_TYPE.GLONASSOFT

  React.useEffect(() => {
    if (isOmnicomm) {
      const currentServer =
        omnicommStore.server || DEFAULT_SERVERS[SYSTEM_TYPE.OMNICOMM]
      setSelectedServer(currentServer)
    } else if (isGelios) {
      const currentServer =
        geliosStore.server || DEFAULT_SERVERS[SYSTEM_TYPE.GELIOS]
      setSelectedServer(currentServer)
    } else if (isFortMonitor) {
      const currentServer =
        fortMonitorStore.server || DEFAULT_SERVERS[SYSTEM_TYPE.FORTMONITOR]
      setSelectedServer(currentServer)
    } else if (isGlonassoft) {
      const currentServer =
        glonassoftStore.server || DEFAULT_SERVERS[SYSTEM_TYPE.GLONASSOFT]
      setSelectedServer(currentServer)
    }
  }, [
    isOmnicomm,
    isGelios,
    isFortMonitor,
    isGlonassoft,
    omnicommStore.server,
    geliosStore.server,
    fortMonitorStore.server,
    glonassoftStore.server,
  ])

  const getSystemConfig = () => {
    if (systemType === SYSTEM_TYPE.OMNICOMM) {
      return {
        name: 'Omnicomm',
        logo: '/images/omnicomm_logo.svg',
        defaultServer: DEFAULT_SERVERS[SYSTEM_TYPE.OMNICOMM],
        showServerButton: true,
        store: omnicommStore,
        buttonText: 'Продолжить',
      }
    } else if (systemType === SYSTEM_TYPE.GELIOS) {
      return {
        name: 'Gelios',
        logo: '/images/gelios_logo.svg',
        defaultServer: DEFAULT_SERVERS[SYSTEM_TYPE.GELIOS],
        showServerButton: true,
        store: geliosStore,
        buttonText: 'Продолжить',
      }
    } else if (systemType === SYSTEM_TYPE.FORTMONITOR) {
      return {
        name: 'FortMonitor',
        logo: '/images/fortmonitor_logo.svg',
        defaultServer: DEFAULT_SERVERS[SYSTEM_TYPE.FORTMONITOR],
        showServerButton: true,
        store: fortMonitorStore,
        buttonText: 'Продолжить',
      }
    } else if (systemType === SYSTEM_TYPE.GLONASSOFT) {
      return {
        name: 'ГЛОНАССSoft',
        logo: '/images/glonassoft_logo.svg',
        defaultServer: DEFAULT_SERVERS[SYSTEM_TYPE.GLONASSOFT],
        showServerButton: true,
        store: glonassoftStore,
        buttonText: 'Продолжить',
      }
    } else {
      return {
        name: 'Axenta',
        logo: '/images/axenta_auth_logo.svg',
        defaultServer: DEFAULT_SERVERS[SYSTEM_TYPE.AXENTA],
        showServerButton: false,
        store: axentaStore,
        buttonText: 'Продолжить',
      }
    }
  }
  const config = getSystemConfig()

  const handleSubmit = async (values: SystemAuthFormData) => {
    setIsLoading(true)
    setError(null)

    const result = await onAuthSuccess(values)

    if (!result.success) {
      setError(result.error)
    }

    setIsLoading(false)
  }

  const handleServerButtonClick = () => {
    if (isOmnicomm) {
      setSelectedServer(
        omnicommStore.server || DEFAULT_SERVERS[SYSTEM_TYPE.OMNICOMM]
      )
    } else if (isGelios) {
      setSelectedServer(
        geliosStore.server || DEFAULT_SERVERS[SYSTEM_TYPE.GELIOS]
      )
    } else if (isFortMonitor) {
      setSelectedServer(
        fortMonitorStore.server || DEFAULT_SERVERS[SYSTEM_TYPE.FORTMONITOR]
      )
    } else if (isGlonassoft) {
      setSelectedServer(
        glonassoftStore.server || DEFAULT_SERVERS[SYSTEM_TYPE.GLONASSOFT]
      )
    }
    setIsServerModalVisible(true)
  }

  const handleServerChange = (value: string) => {
    setSelectedServer(value)
  }

  const handleServerSave = () => {
    if (isOmnicomm) {
      omnicommStore.setServer(selectedServer)
    } else if (isGelios) {
      geliosStore.setServer(selectedServer)
    } else if (isFortMonitor) {
      fortMonitorStore.setServer(selectedServer)
    } else if (isGlonassoft) {
      glonassoftStore.setServer(selectedServer)
    }
    setIsServerModalVisible(false)
  }

  const handleServerModalCancel = () => {
    if (isOmnicomm) {
      setSelectedServer(
        omnicommStore.server || DEFAULT_SERVERS[SYSTEM_TYPE.OMNICOMM]
      )
    } else if (isGelios) {
      setSelectedServer(
        geliosStore.server || DEFAULT_SERVERS[SYSTEM_TYPE.GELIOS]
      )
    } else if (isFortMonitor) {
      setSelectedServer(
        fortMonitorStore.server || DEFAULT_SERVERS[SYSTEM_TYPE.FORTMONITOR]
      )
    } else if (isGlonassoft) {
      setSelectedServer(
        glonassoftStore.server || DEFAULT_SERVERS[SYSTEM_TYPE.GLONASSOFT]
      )
    }
    setIsServerModalVisible(false)
  }

  const handleResetToDefault = () => {
    if (isOmnicomm) {
      setSelectedServer(DEFAULT_SERVERS[SYSTEM_TYPE.OMNICOMM])
    } else if (isGelios) {
      setSelectedServer(DEFAULT_SERVERS[SYSTEM_TYPE.GELIOS])
    } else if (isFortMonitor) {
      setSelectedServer(DEFAULT_SERVERS[SYSTEM_TYPE.FORTMONITOR])
    } else if (isGlonassoft) {
      setSelectedServer(DEFAULT_SERVERS[SYSTEM_TYPE.GLONASSOFT])
    }
  }

  const title = isOmnicomm
    ? 'Вход в Omnicomm'
    : isGelios
      ? 'Вход в Gelios'
      : isFortMonitor
        ? 'Вход в FortMonitor'
        : isGlonassoft
          ? 'Вход в ГЛОНАССSoft'
          : 'Вход в Axenta'

  const modalTitle = isOmnicomm
    ? 'Подключение к серверу Omnicomm'
    : isGelios
      ? 'Подключение к серверу Gelios'
      : isFortMonitor
        ? 'Подключение к серверу FortMonitor'
        : isGlonassoft
          ? 'Подключение к серверу ГЛОНАССSoft'
          : ''

  const showServerModal =
    isOmnicomm || isGelios || isFortMonitor || isGlonassoft

  return (
    <>
      <AuthContainer justify="center" align="center">
        {isMobile && (
          <LoginHeader align="center">
            <Title level={4}>{title}</Title>
          </LoginHeader>
        )}
        <FormContainer vertical gap={48}>
          <Flex vertical gap={18}>
            <Logo className={config.name} src={config.logo} alt={config.name} />

            {error ? (
              <Text type="danger" style={{ textAlign: 'center' }}>
                {error}
              </Text>
            ) : (
              isGlonassoft && (
                <Text
                  type="secondary"
                  style={{
                    maxWidth: '320px',
                    alignSelf: 'center',
                    textAlign: 'center',
                  }}
                >
                  Введите данные от учетной записи откуда будет производиться
                  перенос
                </Text>
              )
            )}
          </Flex>
          <Form
            form={form}
            name={`${systemType}-auth`}
            onFinish={handleSubmit}
            autoComplete="off"
          >
            <FormWrapper vertical gap={36}>
              <FormInputs vertical gap={36}>
                <FormItem
                  name="login"
                  rules={[
                    { required: true, message: 'Введите имя пользователя' },
                  ]}
                >
                  <MediumInput
                    label="Имя пользователя"
                    suffix={<Icon name="login_user" size={16} />}
                    placeholder="Введите имя пользователя"
                    disabled={isLoading}
                    status={error ? 'error' : undefined}
                  />
                </FormItem>

                <FormItem
                  name="password"
                  rules={[{ required: true, message: 'Введите пароль' }]}
                >
                  <MediumInput
                    label="Пароль"
                    type="password"
                    placeholder="Введите пароль"
                    disabled={isLoading}
                    visibilityToggle
                    status={error ? 'error' : undefined}
                  />
                </FormItem>
              </FormInputs>

              <Flex gap={16} style={{ width: '100%' }}>
                <PrimaryButton
                  $fullWidth
                  htmlType="submit"
                  loading={isLoading}
                  disabled={isLoading}
                  style={{ flex: 1 }}
                >
                  {config.buttonText}
                </PrimaryButton>
                {config.showServerButton && (
                  <PrimaryButton
                    style={{ width: '40px' }}
                    icon={<Icon name="login_server" size={16} />}
                    onClick={handleServerButtonClick}
                    disabled={isLoading}
                    title={`Текущий сервер: ${
                      isOmnicomm
                        ? omnicommStore.server ||
                          DEFAULT_SERVERS[SYSTEM_TYPE.OMNICOMM]
                        : isGelios
                          ? geliosStore.server ||
                            DEFAULT_SERVERS[SYSTEM_TYPE.GELIOS]
                          : isFortMonitor
                            ? fortMonitorStore.server ||
                              DEFAULT_SERVERS[SYSTEM_TYPE.FORTMONITOR]
                            : glonassoftStore.server ||
                              DEFAULT_SERVERS[SYSTEM_TYPE.GLONASSOFT]
                    }`}
                  />
                )}
              </Flex>
            </FormWrapper>
          </Form>
        </FormContainer>
        <BackBtn
          size="small"
          style={{ width: '32px' }}
          onClick={onBack}
          icon={<Icon name="closed_modal_phone" size={16} />}
        />
      </AuthContainer>
      {showServerModal && (
        <ServerModal
          title={modalTitle}
          open={isServerModalVisible}
          selectedServer={selectedServer}
          onSave={handleServerSave}
          onCancel={handleServerModalCancel}
          onChange={handleServerChange}
          onResetToDefault={handleResetToDefault}
          placeholder="Введите адрес сервера"
        />
      )}
    </>
  )
}
